import clsx from 'clsx';
import * as React from 'react';

import { Tooltip } from '../Tooltip';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

// For elements that are positioned absolute over an avatar, the size of the badge should be 2/3 of the avatar size
export const badgeOverAvatarSize = {
    xs: 9,
    sm: 16,
    md: 21,
    lg: 32,
};

export interface AvatarProps {
    /**
     * The display name of the user
     */
    displayName: string;

    /**
     * The user avatar image URL
     */
    photoURL?: string | null;

    /**
     * For users without an avatar, use these colors
     */
    fallbackColors: { background: string; text: string };

    /**
     * The size of the avatar
     */
    size?: AvatarSize | number;

    /** Optional styling for the avatar container */
    className?: string;

    /** Show a tooltip with the the user's name
     * @default true
     */
    tooltip?: boolean;

    /** Content of the tooltip */
    tooltipLabel?: string;

    /** Prepend a given string before the username */
    tooltipPrefix?: string;

    /** Optional color to use for the avatar border */
    borderColor?: string;

    /** Whether to use a squared avatar */
    squared?: boolean;
}

const avatarSizes = {
    xs: {
        dimensions: styles.xs,
        text: 'text-[0.5rem] font-medium',
        borderWidth: 1,
    },
    sm: {
        dimensions: styles.sm,
        text: 'text-ui-small',
        borderWidth: 2,
    },
    md: {
        dimensions: styles.md,
        text: 'text-ui-base',
        borderWidth: 2,
    },
    lg: {
        dimensions: styles.lg,
        text: 'text-ui-heading-medium',
        borderWidth: 2,
    },
};

function getAvatarSizeFromPixelValue(pixels: number) {
    if (pixels <= 16) {
        return 'xs';
    }
    if (pixels <= 24) {
        return 'sm';
    }
    return 'md';
}

/**
 * Resolve avatar size to set dimensions for avatar and text inside.
 */
export function resolveAvatarSize(size: AvatarSize | number | undefined) {
    if (!size) {
        return {
            dimensions: 'size-[1em]',
            text: 'text-[0.55em]',
            borderWidth: 2,
        };
    }
    const resolved = typeof size === 'number' ? getAvatarSizeFromPixelValue(size) : size;
    return avatarSizes[resolved];
}

/**
 * Avatar for a user.
 */
export const Avatar = React.forwardRef(function AvatarInnerWithRef(
    props: AvatarProps,
    ref: React.Ref<HTMLDivElement>
) {
    const {
        displayName,
        photoURL,
        className,
        tooltip = true,
        borderColor,
        squared = false,
        fallbackColors,
        tooltipPrefix,
        tooltipLabel = `${tooltipPrefix ? `${tooltipPrefix} ` : ''}${displayName}`,
    } = props;

    const size = resolveAvatarSize(props.size);

    // Squared avatars are only used for images and when explicitly requested
    const hasSquaredBorder = squared && photoURL;

    const border = {
        ...(borderColor
            ? {
                  borderColor,
                  borderWidth: size.borderWidth,
                  borderStyle: 'solid',
              }
            : null),
    };

    const anonymousAvatar = {
        ...(!photoURL
            ? {
                  backgroundColor: fallbackColors?.background,
                  color: fallbackColors?.text,
              }
            : null),
    };

    return (
        <Tooltip disabled={!tooltip} label={tooltipLabel}>
            <div
                ref={ref}
                className={clsx(
                    styles.avatar,
                    hasSquaredBorder ? styles.squared : null,
                    size.dimensions,
                    className
                )}
                style={border}
            >
                {photoURL ? (
                    // Avatar
                    <img
                        src={photoURL}
                        className={clsx(
                            styles.image,
                            hasSquaredBorder ? styles.squared : null,
                            size.dimensions
                        )}
                        alt={displayName}
                    />
                ) : (
                    // No avatar
                    <div className={clsx(styles.avatar, size.dimensions)} style={anonymousAvatar}>
                        <span className={clsx('font-normal', size.text)}>
                            {getInitials(displayName)}
                        </span>
                    </div>
                )}
            </div>
        </Tooltip>
    );
});

// If the displayName is a single uninterrupted string, use the first 2 chars. If there are space-separated
// words, use the first char of the first two words.
function getInitials(displayName: string) {
    const words = displayName.trim().split(/\s+/);

    if (words.length > 1) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
}
